import { AddIcon, Alert, Autocomplete, Button, Stack, type TextFieldProps } from '@all-manager/design-system-v2';
import { useProviderAutocomplete } from "./useProviderAutocomplete";

type Props = Pick<TextFieldProps, 'label' | 'helperText' | 'error' | 'required' | 'autoFocus' | 'fullWidth' | 'size' | 'disabled'> & {
  value: string | null;
  onChange: (value: string | null) => void;
  onChangeText?: (value: string) => void;
  defaultSearch?: string | null;
}

type ProviderOption = ReturnType<typeof useProviderAutocomplete>['options'][number];

export default function ProviderAutocomplete(props: Props) {
  const {
    options,
    selectedProvider,
    isLoading,
    inputValue,
    setInputValue,
    handleCreateProvider,
    isPosting
  } = useProviderAutocomplete(props.value, props.onChange, props.defaultSearch);

  const showCreateHelp = inputValue.length > 0 && !props.value;
  const hasExactMatch = options.some((option) => option.name === inputValue);

  return (
    <Stack spacing={0.5}>
      <Autocomplete<ProviderOption, false, false, true>
        size={props.size}
        options={options}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
        isOptionEqualToValue={(option, value) => typeof value !== 'string' && option.id === value.id}
        value={selectedProvider || null}
        inputValue={inputValue}
        onChange={(_event, newValue) => {
          props.onChange(newValue && typeof newValue !== 'string' ? newValue.id : null);
        }}
        freeSolo
        onInputChange={(_event, newInputValue, reason) => {
          setInputValue(newInputValue);
          props.onChangeText?.(newInputValue);
          if (reason === 'input') {
            props.onChange(null);
          }
        }}
        loading={isLoading}
        noOptionsText={inputValue.length > 0 ? "Aucun prestataire trouvé" : "Commencez à taper pour rechercher un prestataire"}
        loadingText="Recherche en cours…"
        label={props.label}
        helperText={props.helperText}
        error={props.error}
        required={props.required}
        autoFocus={props.autoFocus}
        fullWidth={props.fullWidth}
        disabled={props.disabled}
        placeholder="Rechercher un prestataire…"
      />

      {showCreateHelp ? (
        hasExactMatch ? (
          <Alert severity="warning">
            {`Le prestataire « ${inputValue} » existe déjà. Sélectionnez-le dans la liste ci-dessus.`}
          </Alert>
        ) : (
          <Alert severity="info">
            <Stack spacing={1} alignItems="flex-start">
              {`Aucun prestataire ne correspond à « ${inputValue} ».`}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleCreateProvider}
                loading={isPosting}
              >
                Créer le prestataire
              </Button>
            </Stack>
          </Alert>
        )
      ) : null}
    </Stack>
  );
}
